import { triniumApi } from '@api/Trinium/slice';
import { Job, PatchJobRequest, UpdateJobLocationsRequest } from './types';
import { consignmentSlice } from '../Consignment/slice';
import { applyLocationToJobLegsInDispatchCache, updateOrderInDispatchCache } from '../Dispatch/types';
import { applyLocationToJobLegsInConsignmentCache } from '../Leg/types';

export const jobSlice = triniumApi.injectEndpoints({
  endpoints(builder) {
    return {
      patchJob: builder.mutation<Job, { terminalNumber: string; request: PatchJobRequest; skipCacheUpdate?: boolean }>({
        query: ({
          terminalNumber,
          request: {
            id,
            category,
            vessel,
            voyage,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            legIds: _removeFromPayload,
            ...payload
          }
        }) => ({
          url: `/${terminalNumber}/jobs/${String(id)}`,
          method: 'PATCH',
          body: {
            ...payload,
            ...(category ? { cat: category.id } : {}),
            ...(vessel !== undefined ? { vslId: vessel?.id ?? null } : {}),
            ...(voyage !== undefined ? { voyageId: voyage?.id ?? null } : {})
          }
        }),
        async onQueryStarted(params, { queryFulfilled, dispatch }) {
          const {
            data: { cat, voyageId, vslId, ...jobFields }
          } = await queryFulfilled;

          if (params.skipCacheUpdate) return;

          /** Consignments  */
          dispatch(
            consignmentSlice.util.updateQueryData(
              'fetchConsignment',
              {
                terminalNumber: params.terminalNumber,
                consignmentId: Number(params.request.consignmentId)
              },
              (draft) => {
                Object.assign(draft, {
                  job: {
                    ...draft.job,
                    ...jobFields,
                    catId: cat,
                    // Job catName/TransType won't be on the response, so use the parameter passed in if present
                    catName: params.request.category?.name ?? draft.job.catName,
                    catTransType: params.request.category?.transType ?? draft.job.catTransType,
                    ...(params.request.vessel !== undefined
                      ? {
                          // The Vessel ID is always returned in the response but no name which was causing the name to disappear
                          vessel: vslId
                            ? {
                                id: vslId,
                                name: params.request.vessel?.name ?? draft.job.vessel?.name
                              }
                            : null
                        }
                      : {}),
                    ...(params.request.voyage !== undefined
                      ? {
                          // The Vessel ID is always returned in the response but no details which was causing them to disappear
                          voyage: voyageId
                            ? {
                                id: voyageId,
                                ...draft.job.voyage,
                                ...params.request.voyage
                              }
                            : null
                        }
                      : {})
                  }
                });

                // Update the job fields that are stamped on the leg records too..
                draft.consignmentLegs.forEach((leg) => {
                  if (jobFields.bookingRefNo !== undefined && leg.job) {
                    leg.job.bookingRefNo = jobFields.bookingRefNo;
                  }
                  leg.csn.masterBol = jobFields.masterBol ?? leg.csn.masterBol;
                });
              }
            )
          );

          params.request.legIds?.forEach((legId) => {
            updateOrderInDispatchCache({
              legId: legId,
              jobId: params.request.id,
              bookingRefNo: jobFields.bookingRefNo,
              masterBol: jobFields.masterBol
            });
          });
        }
      }),

      validateBillofLadingNo: builder.query<{ message: string }, { terminalNumber: string; masterBol: string }>({
        query: ({ terminalNumber, masterBol }) => ({
          url: `/${terminalNumber}/jobs/validateMasterBol?masterBol=${encodeURIComponent(masterBol)}`
        })
      }),

      validateBookingRefNo: builder.query<{ message: string }, { terminalNumber: string; bookingRefNo: string }>({
        query: ({ terminalNumber, bookingRefNo }) => ({
          url: `/${terminalNumber}/jobs/validateBookingRefNo?bookingRefNo=${encodeURIComponent(bookingRefNo)}`
        })
      }),

      putJob: builder.mutation<{ invNo: number }, { terminalNumber: string; jobId: number; csnId: number; action: string }>({
        query: ({ terminalNumber, jobId, action }) => ({
          url: `/${terminalNumber}/jobs/${String(jobId)}`,
          method: 'PUT',
          body: { action }
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: 'charges', id: arg.csnId }]
      }),

      updateJobLocations: builder.mutation<null, UpdateJobLocationsRequest>({
        query: ({ terminalNumber, jobId, locations }) => ({
          url: `/${terminalNumber}/jobs/${String(jobId)}`,
          method: 'PUT',
          body: { action: 'updateLocations', data: { locationIds: locations.map((location) => location.id) } }
        }),
        async onQueryStarted({ jobId, locations }, { queryFulfilled }) {
          await queryFulfilled;

          locations.forEach((location) => {
            applyLocationToJobLegsInConsignmentCache(jobId, location);
            applyLocationToJobLegsInDispatchCache(jobId, location);
          });
        }
      })
    };
  }
});

export const { usePatchJobMutation, usePutJobMutation, useUpdateJobLocationsMutation } = jobSlice;
